import { Modal } from "@mantine/core"
import React, { useContext, useEffect, useState } from "react"
import { Trans, useTranslation } from "react-i18next"
import {
	useAddItemToSubscription,
	useGetPriceWithTax,
	useGetProratedPriceWithTax,
} from "../../hooks/usePaddleHooks.js"
import { CoolButton } from "../CoolButton.jsx"
import { Spinner } from "../Spinner.jsx"
import { GlobalStateContext } from "../providers/GlobalStateProvider.jsx"
import { Button } from "@shopify/polaris"

export const UpgradeProtectModal = ({ open, setOpen }) => {
	const { state } = useContext(GlobalStateContext)
	const { t } = useTranslation()
	const addItemToSubscription = useAddItemToSubscription()
	const getProratedPriceWithTax = useGetProratedPriceWithTax()
	const getPriceWithTax = useGetPriceWithTax()

	const [loading, setLoading] = useState(false)
	const [error, setError] = useState(null)
	const [showSuccess, setShowSuccess] = useState(false)

	const [loadingPriceWithTax, setLoadingPriceWithTax] = useState(false)
	const [priceWithTax, setPriceWithTax] = useState(null)

	const [loadingProratedPriceWithTax, setLoadingProratedPriceWithTax] =
		useState(false)
	const [proratedPriceWithTax, setProratedPriceWithTax] = useState(null)

	const activeSubscription = state.kopyBasicSubscription || null
	const billingInterval =
		activeSubscription?.billingInterval === "monthly"
			? "monthly"
			: activeSubscription?.billingInterval === "yearly"
				? "yearly"
				: activeSubscription?.billingInterval === "quarterly"
					? "quarterly"
					: null

	const applicableKopyProtectPriceId =
		activeSubscription?.billingInterval === "monthly"
			? state.pricing?.["kopy_protect"]?.["monthly"]?.[
					state.hasTrialedKopyProtectBefore
						? "no_trial"
						: "with_trial"
				]?.priceId
			: activeSubscription?.billingInterval === "yearly"
				? state.pricing?.["kopy_protect"]?.["yearly"]?.[
						state.hasTrialedKopyProtectBefore
							? "no_trial"
							: "with_trial"
					]?.priceId
				: activeSubscription?.billingInterval === "quarterly"
					? state.pricing?.["kopy_protect"]?.["quarterly"]?.[
							state.hasTrialedKopyProtectBefore
								? "no_trial"
								: "with_trial"
						]?.priceId
					: null
	const applicableKopyProtectPriceInUsd =
		activeSubscription?.billingInterval === "monthly"
			? state.pricing?.["kopy_protect"]?.["monthly"]?.[
					state.hasTrialedKopyProtectBefore
						? "no_trial"
						: "with_trial"
				]?.priceInUsd
			: activeSubscription?.billingInterval === "yearly"
				? state.pricing?.["kopy_protect"]?.["yearly"]?.[
						state.hasTrialedKopyProtectBefore
							? "no_trial"
							: "with_trial"
					]?.priceInUsd
				: activeSubscription?.billingInterval === "quarterly"
					? state.pricing?.["kopy_protect"]?.["quarterly"]?.[
							state.hasTrialedKopyProtectBefore
								? "no_trial"
								: "with_trial"
						]?.priceInUsd
					: null

	const _updateSubscription = async () => {
		setError(null)

		if (!applicableKopyProtectPriceId) {
			setError("Kopy Protect Price Id not found")
			return
		}

		setLoading(true)

		const { success, errorCode } = await addItemToSubscription({
			item: "protect",
			priceId: applicableKopyProtectPriceId,
		})
		setLoading(false)

		if (success) {
			setShowSuccess(true)
			setTimeout(() => {
				setLoading(true)
			}, 1000 * 2)
			setTimeout(() => {
				window.location.reload()
			}, 1000 * 10)
		} else {
			setError(
				`Failed to upgrade subscription${errorCode === "subscription_payment_declined" ? ": Payment Declined" : ""}`,
			)
		}
	}

	useEffect(() => {
		const activeSubscriptionId = activeSubscription?.subscriptionId || null

		if (applicableKopyProtectPriceId && activeSubscriptionId) {
			setLoadingProratedPriceWithTax(true)

			getProratedPriceWithTax({
				subscriptionId: activeSubscriptionId,
				newSubscriptionItem: {
					price_id: applicableKopyProtectPriceId,
					quantity: 1,
				},
			}).then(({ proratedPriceWithTax }) => {
				setLoadingProratedPriceWithTax(false)
				if (proratedPriceWithTax) {
					setProratedPriceWithTax(proratedPriceWithTax)
				}
			})
		}
	}, [])

	useEffect(() => {
		if (state.paddleCustomerId && applicableKopyProtectPriceId) {
			setLoadingPriceWithTax(true)

			getPriceWithTax({
				paddleCustomerId: state.paddleCustomerId,
				priceId: applicableKopyProtectPriceId,
			}).then(({ priceWithTax }) => {
				setLoadingPriceWithTax(false)
				if (priceWithTax) {
					setPriceWithTax(priceWithTax)
				}
			})
		}
	}, [state.paddleCustomerId])

	return (
		<Modal
			title={
				<div
					style={{
						display: "flex",
						gap: "0.3rem",
						alignItems: "center",
						color: "#333",
					}}
				>
					<svg width="1.8rem" height="1.8rem" viewBox="0 0 24 24">
						<path
							fill="currentColor"
							d="M8 20q-.425 0-.712-.288T7 19t.288-.712T8 18h8q.425 0 .713.288T17 19t-.288.713T16 20zm4-4q-.425 0-.712-.288T11 15V7.825L9.1 9.7q-.275.275-.687.288T7.7 9.7q-.275-.275-.275-.7t.275-.7l3.6-3.6q.15-.15.325-.212T12 4.425t.375.063t.325.212l3.6 3.6q.275.275.288.688T16.3 9.7q-.275.275-.7.275t-.7-.275L13 7.825V15q0 .425-.287.713T12 16"
						></path>
					</svg>
					<span style={{ fontSize: "1.4rem", fontWeight: "700" }}>
						{t("protect.upgrade_title")}
					</span>
				</div>
			}
			opened={open}
			onClose={() => setOpen(false)}
			centered
			radius="lg"
			size="30rem"
			classNames={{
				content: "border shadow",
			}}
		>
			<div
				style={{
					marginTop: "2rem",
					marginBottom: "2rem",
					fontSize: "1.2rem",
					color: "#333",
					fontWeight: "700",
				}}
			>
				{t("protect.upgrade_body_header")}
			</div>

			{state.hasTrialedKopyProtectBefore === false && (
				<div
					style={{
						background: "#12a112",
						marginLeft: "auto",
						marginRight: "0.5rem",
						marginBottom: "0.2rem",
						width: "fit-content",
						padding: "0.3rem 0.4rem",
						borderRadius: "1rem",
					}}
				>
					<div
						style={{
							fontSize: "0.67rem",
							color: "#fff",
							lineHeight: "1",
						}}
					>
						{t("protect.includes_3_day_free_trial")}
					</div>
				</div>
			)}

			<div
				style={{
					background: "#F0F0F0",
					padding: "0.5rem",
					borderRadius: "1rem",
					marginBottom: "3rem",
				}}
			>
				<div
					style={{
						display: "flex",
						gap: "0.7rem",
						alignItems: "center",
						justifyContent: "start",
					}}
				>
					<div
						className="shadow"
						style={{
							padding: "0.3rem",
							borderRadius: "0.5rem",
							background: "#fff",
							color: "#333",
						}}
					>
						<svg
							style={{
								display: "block",
								height: "1.2rem",
								width: "1.2rem",
							}}
							viewBox="0 0 512 512"
						>
							<path
								fill="currentColor"
								d="M256 0c4.6 0 9.2 1 13.4 2.9l188.3 79.9c22 9.3 38.4 31 38.3 57.2c-.5 99.2-41.3 280.7-213.6 363.2c-16.7 8-36.1 8-52.8 0C57.3 420.7 16.5 239.2 16 140c-.1-26.2 16.3-47.9 38.3-57.2L242.7 2.9C246.8 1 251.4 0 256 0m0 66.8v378C394 378 431.1 230.1 432 141.4z"
							></path>
						</svg>
					</div>

					<div>
						<p
							style={{
								fontSize: "0.85rem",
								fontWeight: "700",
								color: "#333",
							}}
						>
							Kopy Protect
						</p>
						<p
							style={{
								fontSize: "0.8rem",
								fontWeight: "500",
								color: "#777",
							}}
						>
							{t("protect.upgrade_body_short_description")}
						</p>
					</div>

					<div
						style={{
							marginLeft: "auto",
							display: "flex",
							flexDirection: "column",
							gap: "0.1rem",
							alignItems: "end",
						}}
					>
						{loadingPriceWithTax ? (
							<Spinner />
						) : (
							<div>
								<p
									style={{
										fontSize: "0.85rem",
										fontWeight: "700",
										lineHeight: "1",
										color: "#777",
									}}
								>
									{new Intl.NumberFormat("en-US", {
										style: "currency",
										currency: "USD",
									}).format(
										priceWithTax ||
											applicableKopyProtectPriceInUsd,
									)}{" "}
									{billingInterval === "monthly"
										? t("account.month")
										: billingInterval === "yearly"
											? t("account.year")
											: billingInterval === "quarterly"
												? t("account.quarterly")
												: null}
								</p>
								{!priceWithTax ||
								applicableKopyProtectPriceInUsd >=
									priceWithTax ? (
									<p
										style={{
											fontSize: "0.7rem",
											fontWeight: "400",
											lineHeight: "1",
											color: "#888",
											textAlign: "end",
										}}
									>
										{t("protect.plus_tax")}
									</p>
								) : null}
							</div>
						)}
					</div>
				</div>
			</div>

			{error ? (
				<div
					style={{
						background: "#ff2f2f",
						padding: "1rem",
						borderRadius: "0.5rem",
						marginBottom: "1rem",
					}}
				>
					<p
						style={{
							fontSize: "0.9rem",
							fontWeight: "600",
							color: "#fff",
							marginBottom: "1rem",
						}}
					>
						{error}
					</p>
					<p
						style={{
							fontSize: "0.85rem",
							fontWeight: "500",
							color: "#fff",
							marginBottom: "1rem",
						}}
					>
						If upgrading keeps failing, please check that you have a
						valid payment method.
					</p>
					{activeSubscription?.updatePaymentMethodUrl && (
						<Button
							fullWidth
							onClick={() =>
								window.open(
									activeSubscription?.updatePaymentMethodUrl,
									"_blank",
								)
							}
						>
							Update Payment Method
						</Button>
					)}
				</div>
			) : null}

			<CoolButton
				onClick={loading || showSuccess ? null : _updateSubscription}
			>
				<span style={{ fontWeight: "700" }}>
					{loading ? (
						<Spinner />
					) : showSuccess ? (
						<svg width="1rem" height="1rem" viewBox="0 0 512 512">
							<path
								d="M256 32C132.3 32 32 132.3 32 256s100.3 224 224 224 224-100.3 224-224S379.7 32 256 32zm114.9 149.1L231.8 359.6c-1.1 1.1-2.9 3.5-5.1 3.5-2.3 0-3.8-1.6-5.1-2.9-1.3-1.3-78.9-75.9-78.9-75.9l-1.5-1.5c-.6-.9-1.1-2-1.1-3.2 0-1.2.5-2.3 1.1-3.2.4-.4.7-.7 1.1-1.2 7.7-8.1 23.3-24.5 24.3-25.5 1.3-1.3 2.4-3 4.8-3 2.5 0 4.1 2.1 5.3 3.3 1.2 1.2 45 43.3 45 43.3l111.3-143c1-.8 2.2-1.4 3.5-1.4 1.3 0 2.5.5 3.5 1.3l30.6 24.1c.8 1 1.3 2.2 1.3 3.5.1 1.3-.4 2.4-1 3.3z"
								fill="#1ad313"
							></path>
						</svg>
					) : (
						<>
							{state.hasTrialedKopyProtectBefore
								? t("common.upgrade_now")
								: t("protect.upgrade.start_3_day_free_trial")}
							{proratedPriceWithTax ? (
								<Trans
									i18nKey="upgrade_basic.until_next_renewal"
									values={{
										price: new Intl.NumberFormat("en-US", {
											style: "currency",
											currency: "USD",
										}).format(proratedPriceWithTax),
									}}
								></Trans>
							) : null}
						</>
					)}
				</span>
			</CoolButton>

			{showSuccess && loading ? (
				<p
					style={{
						marginTop: "0.5rem",
						fontSize: "0.8rem",
						color: "#888",
						width: "fit-content",
						marginLeft: "auto",
						marginRight: "auto",
					}}
				>
					{t("protect.upgrade_success_redirecting")}
				</p>
			) : null}
		</Modal>
	)
}
